import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

type Range = '7d' | '30d' | '90d' | 'all';

interface TrendPoint {
  date: string;
  cash: string;
  orders: number;
}

interface Dashboard {
  range: Range;
  range_start: string | null;
  generated_at: string;
  cash_inflow: {
    total_amount: string;
    currency: string;
    transaction_count: number;
    product_total: string;
    product_count: number;
    consultation_total: string;
    consultation_count: number;
  };
  orders: {
    total: number;
    product_count: number;
    consultation_count: number;
    by_status: Record<string, number>;
  };
  products: {
    active: number;
    new_in_range: number;
  };
  users: {
    total: number;
    by_role: Record<string, number>;
    pending_kyc: number;
  };
  time_series: TrendPoint[];
}

const RANGE_OPTIONS: { value: Range; label: string }[] = [
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: 'all', label: 'All time' },
];

const ROLE_LABELS: Record<string, string> = {
  individual: 'Individuals',
  consultant: 'Consultants',
  store_owner: 'Store owners',
  admin: 'Admins',
  super_admin: 'Super admins',
};

// Tailwind-derived hex palette used across the recharts elements. Picked to
// stay readable on the white card backgrounds.
const ORDER_STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b', // amber-500
  successful: '#3b82f6', // blue-500
  marked_as_packed: '#8b5cf6', // violet-500
  shipped: '#6366f1', // indigo-500
  delivered: '#14b8a6', // teal-500
  settled: '#10b981', // emerald-500
  cancelled: '#94a3b8', // slate-400
  refunded: '#cbd5e1', // slate-300
  failed: '#ef4444', // red-500
  lost: '#b91c1c', // red-700
};
const ROLE_COLORS: Record<string, string> = {
  individual: '#3b82f6',
  consultant: '#10b981',
  store_owner: '#8b5cf6',
  admin: '#f59e0b',
  super_admin: '#ef4444',
};
const FALLBACK_PALETTE = [
  '#3b82f6',
  '#10b981',
  '#f59e0b',
  '#8b5cf6',
  '#14b8a6',
  '#ef4444',
  '#6366f1',
  '#94a3b8',
];

function colorFor(key: string, map: Record<string, string>, idx: number) {
  return map[key] ?? FALLBACK_PALETTE[idx % FALLBACK_PALETTE.length];
}

function formatNaira(numStr: string) {
  const n = Number(numStr);
  if (!Number.isFinite(n)) return numStr;
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatCount(n: number) {
  return new Intl.NumberFormat('en-US').format(n);
}

export function DashboardPage() {
  const [range, setRange] = useState<Range>('30d');
  const [data, setData] = useState<Dashboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<Dashboard>('/back-office/dashboard', { params: { range } })
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(extractErrorMessage(err, 'Failed to load dashboard'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [range]);

  return (
    <div className="px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Dashboard</h1>
          <p className="text-sm text-slate-500">
            Operational health across orders, products, and KYC review.
          </p>
        </div>
        <div className="inline-flex rounded border border-slate-200 bg-white p-1 text-sm">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setRange(opt.value)}
              className={clsx(
                'rounded px-3 py-1.5 transition-colors',
                range === opt.value
                  ? 'bg-brand-500 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              )}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Top tile row */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          title="Cash inflow"
          subtitle={
            data
              ? `${formatCount(data.cash_inflow.product_count)} product · ${formatCount(data.cash_inflow.consultation_count)} consultation`
              : '0 successful transactions'
          }
          value={
            loading
              ? '…'
              : data
                ? formatNaira(data.cash_inflow.total_amount)
                : '—'
          }
          tone="emerald"
        />
        <Tile
          title="Orders & bookings"
          subtitle={
            data
              ? `${formatCount(data.orders.product_count)} product · ${formatCount(data.orders.consultation_count)} consultation`
              : 'All statuses'
          }
          value={loading ? '…' : data ? formatCount(data.orders.total) : '—'}
          tone="blue"
        />
        <Tile
          title="Active products"
          subtitle={
            data
              ? `${formatCount(data.products.new_in_range)} new in range`
              : ''
          }
          value={loading ? '…' : data ? formatCount(data.products.active) : '—'}
          tone="violet"
        />
        <Tile
          title="Users"
          subtitle={
            data
              ? data.users.pending_kyc > 0
                ? `${formatCount(data.users.pending_kyc)} pending KYC review`
                : 'No pending KYC reviews'
              : ''
          }
          value={loading ? '…' : data ? formatCount(data.users.total) : '—'}
          tone="amber"
          footer={
            data && data.users.pending_kyc > 0 ? (
              <Link
                to="/users?pending_review=true"
                className="text-xs font-medium text-brand-600 hover:underline"
              >
                Review pending →
              </Link>
            ) : null
          }
        />
      </div>

      {/* Trend chart */}
      <TrendChart
        points={data?.time_series ?? []}
        loading={loading}
        currencyLabel={data?.cash_inflow.currency ?? 'NGN'}
      />

      {/* Breakdown row */}
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DonutCard
          title="Product orders by status"
          entries={data ? Object.entries(data.orders.by_status) : []}
          loading={loading}
          emptyText="No product orders in range yet."
          formatLabel={(k) => k.replace(/_/g, ' ')}
          colorFor={(k, i) => colorFor(k, ORDER_STATUS_COLORS, i)}
        />
        <DonutCard
          title="Users by role"
          entries={
            data
              ? Object.entries(data.users.by_role).filter(
                  ([, v]) => (v ?? 0) > 0
                )
              : []
          }
          loading={loading}
          emptyText="No users yet."
          formatLabel={(k) => ROLE_LABELS[k] ?? k.replace(/_/g, ' ')}
          colorFor={(k, i) => colorFor(k, ROLE_COLORS, i)}
        />
      </div>

      {data?.generated_at && (
        <div className="mt-6 text-xs text-slate-400">
          Generated {new Date(data.generated_at).toLocaleString()}
          {data.range_start && (
            <> · since {new Date(data.range_start).toLocaleDateString()}</>
          )}
        </div>
      )}
    </div>
  );
}

interface TileProps {
  title: string;
  subtitle?: string;
  value: string;
  tone: 'emerald' | 'blue' | 'violet' | 'amber';
  footer?: React.ReactNode;
}

const TILE_TONE: Record<TileProps['tone'], string> = {
  emerald: 'border-emerald-100 bg-emerald-50',
  blue: 'border-blue-100 bg-blue-50',
  violet: 'border-violet-100 bg-violet-50',
  amber: 'border-amber-100 bg-amber-50',
};

function Tile({ title, subtitle, value, tone, footer }: TileProps) {
  return (
    <div
      className={clsx('rounded border bg-white p-5 shadow-sm', TILE_TONE[tone])}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-slate-600">
        {title}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {subtitle && (
        <div className="mt-1 text-xs text-slate-500">{subtitle}</div>
      )}
      {footer && <div className="mt-3">{footer}</div>}
    </div>
  );
}

function DonutCard({
  title,
  entries,
  loading,
  emptyText,
  formatLabel,
  colorFor,
}: {
  title: string;
  entries: [string, number][];
  loading: boolean;
  emptyText: string;
  formatLabel: (key: string) => string;
  colorFor: (key: string, idx: number) => string;
}) {
  const rows = useMemo(
    () =>
      entries
        .filter(([, n]) => (n ?? 0) > 0)
        .sort((a, b) => b[1] - a[1])
        .map(([key, value], idx) => ({
          key,
          label: formatLabel(key),
          value,
          color: colorFor(key, idx),
        })),
    [entries, formatLabel, colorFor]
  );
  const total = rows.reduce((acc, r) => acc + r.value, 0);

  return (
    <section className="rounded border border-slate-200 bg-white p-5">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="mt-4">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : total === 0 ? (
          <div className="text-sm text-slate-500">{emptyText}</div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-5">
            <div className="relative sm:col-span-2 h-44">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={rows}
                    dataKey="value"
                    nameKey="label"
                    innerRadius="62%"
                    outerRadius="92%"
                    paddingAngle={2}
                    strokeWidth={0}
                  >
                    {rows.map((r) => (
                      <Cell key={r.key} fill={r.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCount(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-xs uppercase tracking-wide text-slate-400">
                  Total
                </div>
                <div className="text-xl font-semibold text-slate-800">
                  {formatCount(total)}
                </div>
              </div>
            </div>
            <ul className="sm:col-span-3 space-y-1.5 self-center">
              {rows.map((r) => {
                const pct = total > 0 ? (r.value / total) * 100 : 0;
                return (
                  <li
                    key={r.key}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="flex items-center gap-2 capitalize text-slate-700">
                      <span
                        className="inline-block h-2.5 w-2.5 rounded-sm"
                        style={{ background: r.color }}
                      />
                      {r.label}
                    </span>
                    <span className="text-slate-500">
                      <span className="font-medium text-slate-900">
                        {formatCount(r.value)}
                      </span>
                      <span className="ml-1.5 text-xs">
                        {pct.toFixed(pct < 10 ? 1 : 0)}%
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

interface TrendChartProps {
  points: TrendPoint[];
  loading: boolean;
  currencyLabel: string;
}

function TrendChart({ points, loading, currencyLabel }: TrendChartProps) {
  const chartData = useMemo(
    () =>
      points.map((p) => ({
        date: p.date,
        // Compact x-axis label: drop the year for readability.
        label: formatShortDate(p.date),
        cash: Number(p.cash || 0),
        orders: p.orders,
      })),
    [points]
  );

  const empty =
    chartData.length === 0 ||
    chartData.every((p) => p.cash === 0 && p.orders === 0);

  return (
    <section className="mt-6 rounded border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
          Cash & orders trend
        </h2>
        {!loading && !empty && (
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-emerald-500" />
              Cash ({currencyLabel})
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2.5 w-2.5 rounded-sm bg-blue-500" />
              Orders
            </span>
          </div>
        )}
      </div>
      <div className="mt-4 h-72">
        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : empty ? (
          <div className="text-sm text-slate-500">
            No activity in the selected range.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={chartData}
              margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
            >
              <defs>
                <linearGradient id="cashFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#64748b' }}
                stroke="#cbd5e1"
                interval="preserveStartEnd"
                minTickGap={20}
              />
              <YAxis
                yAxisId="cash"
                tick={{ fontSize: 11, fill: '#64748b' }}
                stroke="#cbd5e1"
                tickFormatter={(v) => compactNumber(v)}
              />
              <YAxis
                yAxisId="orders"
                orientation="right"
                tick={{ fontSize: 11, fill: '#64748b' }}
                stroke="#cbd5e1"
                allowDecimals={false}
              />
              <Tooltip
                formatter={(value, name) => {
                  if (name === 'cash')
                    return [formatNaira(String(value)), 'Cash'];
                  return [formatCount(Number(value)), 'Orders'];
                }}
                labelFormatter={(label) => {
                  const point = chartData.find((p) => p.label === label);
                  return point
                    ? new Date(point.date).toLocaleDateString()
                    : String(label);
                }}
              />
              <Area
                yAxisId="cash"
                type="monotone"
                dataKey="cash"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#cashFill)"
              />
              <Bar
                yAxisId="orders"
                dataKey="orders"
                fill="#3b82f6"
                fillOpacity={0.7}
                barSize={
                  chartData.length > 60 ? 4 : chartData.length > 30 ? 6 : 12
                }
                radius={[3, 3, 0, 0]}
              />
              <Legend wrapperStyle={{ display: 'none' }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </section>
  );
}

function formatShortDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function compactNumber(n: number) {
  if (Math.abs(n) >= 1_000_000)
    return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (Math.abs(n) >= 1_000)
    return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}
